// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportsStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var fetcher = require('fetchers/Izenda.ReportsDataFetcher');
  var reportDesignerFetcher = require('fetchers/Izenda.ReportDesignerDataFetcher');
  var Reports = require('./models/Izenda.Reports');
  var exportFetcher = require('fetchers/Izenda.ExportFetcher');
  var uiState = require('logic/Izenda.UiState.js');
  var constants = require('logic/data/Izenda.ReportsConstants');
  var LazyLoadingUtils = require('share/utils/Izenda.LazyLoadingUtils');
  var CategoryDetail = require('logic/models/Izenda.CategoryDetail');
  var PrintingUtils = require('share/utils/Izenda.PrintingUtils');
  const { isEmpty, isArray, map, isNil, getOr, get, find } = require('lodash/fp');

  function ReportsStore() {
    this.contentLazyLoadingStore = {};
    this.leftMenuLazyLoadingStore = {};

    var props = {
      model: new Reports(),

      // We show report when this value is not null. Otherwise, we show report list.
      selectedReportId: null,
      report: null,
      reportName: '',
      categoryId: constants.EMPTY_GUID,
      reportType: constants.CATEGORY_TYPE.REPORT,

      version: 0,
      disabled: false,

      // keep selected category/subcategory in left navigation
      selectedCategoryId: null,
      // Previous link in report list
      reportListLink: null,

      reportCategoryTree: function() {
        return this.model.reportCategoryTree;
      },

      items: function() {
        return this.model.items;
      },

      tenantId: function() {
        return uiState.getTenantId();
      },

      isTemplateType: function() {
        return this.reportType === constants.CATEGORY_TYPE.TEMPLATES;
      },

      isUncategorized: function() {
        return this.selectedCategoryId === constants.EMPTY_GUID;
      },

      isRootCategory: function() {
        return (
          this.selectedCategoryId === constants.ALL_CATEGORY_TYPES.ALL_REPORTS.id ||
          this.selectedCategoryId === constants.ALL_CATEGORY_TYPES.ALL_TEMPLATES.id
        );
      }
    };

    m.extendObservable(this, props);
  }

  /**
   * Load all categories
   * @param  {Number} reportType
   * @return {array}
   */
  ReportsStore.prototype.loadAllCategories = m.action(function loadAllCategories(reportType) {
    this.reportType = reportType || constants.CATEGORY_TYPE.REPORT;

    return fetcher.loadAllCategories(this.reportType, this.tenantId).then(
      function(categoryList) {
        this.model.readReportCategoryTree(categoryList, this.reportType);
        return categoryList;
      }.bind(this)
    );
  });

  /**
   * Load categories for copy popup
   * @param  {Number} reportType
   * @return {array}
   */
  ReportsStore.prototype.loadCategoriesForCopyPopup = m.action(function loadAllCategories(reportType, isGlobal) {
    this.reportType = reportType || constants.CATEGORY_TYPE.REPORT;
    var model = this.model;

    return fetcher.loadCategoriesForCopyPopup(this.reportType, this.tenantId, isGlobal).then(function(categoryList) {
      model.readCopyReportCategoryList(categoryList);
      return categoryList;
    });
  });

  /**
   * Load Subcategories by category ID for copy popup
   * @param categoryId
   * @returns {*|promise}
   */
  ReportsStore.prototype.loadSubCategoriesForCopyPopup = function(categoryId) {
    return fetcher.loadSubCategoriesForCopyPopup(categoryId);
  };

  /**
   * validate category name
   * @param  {object} category
   * @returns {Promise}
   */
  ReportsStore.prototype.validateCategoryName = function(category) {
    return fetcher.validateCategoryName(category).then(function(result) {
      return result.messages;
    });
  };

  /**
   * Validate Report Name
   * @param {Object} boardDetail
   * @returns {Promise}
   */
  ReportsStore.prototype.validateName = function(boardDetail) {
    return reportDesignerFetcher.validateReportName(boardDetail).then(function(result) {
      return result.messages;
    });
  };

  /**
   * Check valid report id
   * @param {string} reportId
   * @returns {Promise}
   */
  ReportsStore.prototype.checkValidReportId = function(reportId) {
    return reportDesignerFetcher.validateReportId(reportId);
  };

  /**
   * Validate Report Name
   * @param {Object} boardDetail
   * @returns {Promise}
   */
  ReportsStore.prototype.rename = function(boardDetail) {
    return fetcher
      .renameReport({
        name: boardDetail.name,
        reportKey: {
          key: boardDetail.id
        }
      })
      .then(function(result) {
        return result;
      });
  };

  /**
   * Delete Report
   * @param {Object} boardDetail
   * @param {Object} reportSubCategory
   * @returns {Promise}
   */
  ReportsStore.prototype.deleteReport = function(boardDetail, reportSubCategory, version) {
    var reportId = boardDetail.id;
    var data = this.buildReportKey(reportId);

    return fetcher.deleteReport(data, version).then(
      function(result) {
        return result && this.removeReport(reportId, reportSubCategory);
      }.bind(this)
    );
  };

  /**
   * Remove report
   * @param {type} reportId
   * @param {type} reportSubCategory
   */
  ReportsStore.prototype.removeReport = m.action(function removeReport(reportId, reportSubCategory) {
    var reportItem = find(
      {
        id: reportId
      },
      reportSubCategory.reports
    );
    reportSubCategory.reports.remove(reportItem);

    if (reportSubCategory.reports.length === 0) {
      this.loadLazyLoadingReports(null, this.reportType, true, null);
      this.loadLazyLoadingCategories(this.reportType, true);
    }
    return null;
  });

  /**
   * Delete a ReportCategory by id
   * @param {string} reportCategoryId
   * @returns {promise}
   */
  ReportsStore.prototype.deleteReportCategory = m.action(function deleteReportCategory(dataSource) {
    var reportCategoryId = dataSource.id;
    if (reportCategoryId) {
      return fetcher.deleteReportCategory(reportCategoryId).then(
        m.action(
          function(result) {
            result.success && this.model.removeReportCategory(dataSource);

            // only reload report content if deleted category is activing
            var activeCategoryId = dataSource.isSubCategory ? dataSource.parentId : dataSource.id;
            if ((result.success && activeCategoryId === this.selectedCategoryId) || this.isRootCategory()) {
              return this.selectedCategoryId;
            }
            return null;
          }.bind(this)
        )
      );
    }
    return Promise.resolve({ success: false });
  });

  /**
   * Copy new Report
   * @param {Object} dataObject
   * @param {Object} boardDetail
   * @returns {Promise}
   */
  ReportsStore.prototype.copyNewReport = m.action(function copyNewReport(
    dataObject,
    boardDetail,
    isMoveReport,
    version
  ) {
    var category = dataObject.category || {};
    var subCategory = dataObject.subCategory || {};
    var tenantIdCategory = category.id ? category.tenantId : this.tenantId;
    var tenantIdSubCategory = subCategory.id ? subCategory.tenantId : this.tenantId;

    var data = {
      isGlobal: !!dataObject.saveInto,
      id: boardDetail.id,
      name: dataObject.reportName,
      version: version,
      category: {
        id: String.isGuidFormat(category.id) ? category.id : null,
        name: category.name,
        type: boardDetail.type,
        tenantId: tenantIdCategory
      },
      subCategory: {
        id: String.isGuidFormat(subCategory.id) ? subCategory.id : null,
        name: subCategory.name,
        type: boardDetail.type,
        parentId: String.isGuidFormat(category.id) ? category.id : null,
        tenantId: tenantIdSubCategory
      },
      type: boardDetail.type,
      tenantId: this.tenantId
    };

    var copyModeReportFn = isMoveReport ? fetcher.moveReport : fetcher.copyNewReport;

    return copyModeReportFn(data).then(function(result) {
      return isArray(result.messages) ? result.messages : result;
    });
  });

  /**
   * Update some additional info which was loaded previously after saving/saving as report.
   * @param {ReportDesignerFieldsStore} reportDesignerFiledsStore
   */
  ReportsStore.prototype.updateAddtionalInfo = m.action(function(reportDesignerFiledsStore) {
    this.selectedCategoryId =
      get('subCategoryId', reportDesignerFiledsStore.masterModel) ||
      get('categoryId', reportDesignerFiledsStore.masterModel);
  });

  /**
   * Update selected report id
   */
  ReportsStore.prototype.updateReportId = m.action(function updateReportId(reportId, version) {
    this.selectedReportId = reportId;
    this.version = version || 0;
  });

  /**
   * Get the report information
   * @returns {String}
   */
  ReportsStore.prototype.getReportInfo = function() {
    var reportId = this.selectedReportId;

    if (!reportId) {
      return Promise.resolve('');
    }

    return fetcher.reportInfo(reportId, this.version).then(
      m.action(
        function(result) {
          this.reportType = result.type.toString() || constants.CATEGORY_TYPE.REPORT;
          this.reportName = result.name || '';
          this.categoryId = result.categoryId || constants.EMPTY_GUID;
          if (!this.selectedCategoryId) {
            this.selectedCategoryId = result.subCategoryId || result.categoryId;
          }
          return null;
        }.bind(this)
      )
    );
  };

  /**
   * Build report key object
   */
  ReportsStore.prototype.buildReportKey = function(reportId) {
    return {
      reportKey: {
        key: reportId
      }
    };
  };

  /**
   * export report
   * @param  {string} url
   * @param  {string} format    export format
   * @param  {string} reportId  report id
   * @param  {array}  filters    report filters
   * @param  {object} successCallBack
   * @param  {object} failCallBack
   * @return {Promise}
   */
  ReportsStore.prototype.exportReport = function(
    url,
    format,
    reportId,
    filters,
    exportObj,
    successCallBack,
    failCallBack
  ) {
    var filtersData = map(function(filterItem) {
      return filterItem.getFilterDetails();
    }, filters);

    return exportFetcher.export(
      {
        url: url,
        format: format,
        reportId: reportId,
        filters: JSON.stringify(filtersData),
        draftId: get('draftId', exportObj),
        subReportFilters: JSON.stringify(getOr([], 'subReportFilter', exportObj)),
        queryString: get('queryString', exportObj),
        tenantId: this.tenantId // for export API.
      },
      successCallBack,
      failCallBack
    );
  };

  /**
   * Print report
   * @param  {string} url
   * @param  {string} format    export format
   * @param  {string} reportId  report id
   * @param  {array}  filters    report filters
   * @param  {any} successCallBack
   * @param  {any} failCallBack
   * @return {Promise}
   */
  ReportsStore.prototype.printReport = function(
    url,
    format,
    reportId,
    filters,
    exportObj,
    successCallBack,
    failCallBack
  ) {
    var filtersData = map(function(filterItem) {
      return filterItem.getFilterDetails();
    }, filters);

    function processSuccess(result) {
      successCallBack(result);
      PrintingUtils.printHtml(result);
    }

    return exportFetcher
      .export({
        fileSessionKey: String.newGuid(),
        current_tenant: uiState.getTenantId(),
        url: url,
        format: format,
        reportId: reportId,
        filters: filtersData,
        draftId: get('draftId', exportObj),
        subReportFilters: getOr([], 'subReportFilter', exportObj),
        queryString: get('queryString', exportObj),
        tenantId: this.tenantId // for export API.
      })
      .then(processSuccess, failCallBack);
  };

  /**
   * Send email
   * @param {String} reportId
   * @param {object} email
   * @returns {promise}
   */
  ReportsStore.prototype.sendEmail = function(draftId, email, reportId) {
    return exportFetcher.email({ reportId: reportId, draftId: draftId }, email);
  };

  ReportsStore.prototype.disable = m.action(function disable() {
    this.disabled = true;
  });

  /**
   * reset model to default
   */
  ReportsStore.prototype.resetModel = m.action(function resetModel() {
    this.reportType = constants.CATEGORY_TYPE.REPORT;
    this.selectedReportId = null;
    this.report = null;
  });

  /**
   * update renderingTime
   * @param {Number} renderingTime
   */
  ReportsStore.prototype.updateRenderingTime = function(renderingTime) {
    return fetcher.updateRenderingTime(this.selectedReportId, renderingTime);
  };

  /**
   * Read report from API
   * @param {Object} report data
   */
  ReportsStore.prototype.readReport = function(reportData) {
    this.report = reportData;
  };

  /**
   * Reset report data
   */
  ReportsStore.prototype.resetReportData = function() {
    this.report = null;
  };

  /**
   * Reset report data
   */
  ReportsStore.prototype.savePreviousLink = function(link) {
    this.reportListLink = link;
  };

  /**
   * Load report list
   * @param  {string} catId
   * @param  {string} reportType
   * @return {array}
   */
  ReportsStore.prototype.loadLazyLoadingReports = m.action(function loadLazyLoadingReports(
    catId,
    reportType,
    isReset,
    isGlobal,
    pageInfo
  ) {
    this.selectedCategoryId = catId;
    pageInfo = pageInfo || this.contentLazyLoadingStore.pageInfoForFirstTime;
    isGlobal = this.convertIsGlobal(isGlobal);

    var dataObject = {
      tenantId: this.tenantId,
      type: reportType,
      isUncategorized: this.isUncategorized,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true,
      isGlobal: isGlobal
    };

    if (!this.isRootCategory && !this.isUncategorized) {
      dataObject.criterias = [
        {
          key: constants.CATEGORY_TYPE.CATEGORY_ID,
          value: this.selectedCategoryId
        }
      ];
    }

    return fetcher
      .lazyLoadingReports(dataObject)
      .bind(this)
      .then(
        m.action(function(result) {
          this.items.populate(result, isReset, constants.REPORT_LIST_CONCAT_TREE_MAP);
        })
      );
  });

  /**
   * Load all categories
   * @param  {Number} reportType
   * @return {array}
   */
  ReportsStore.prototype.loadLazyLoadingCategories = m.action(function loadLazyLoadingCategories(
    reportType,
    isReset,
    pageInfo
  ) {
    pageInfo = pageInfo || this.leftMenuLazyLoadingStore.pageInfoForFirstTime;
    this.reportType = reportType || constants.CATEGORY_TYPE.REPORT;
    var param = {
      tenantId: this.tenantId,
      type: this.reportType,
      isUncategorized: this.isUncategorized,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true
    };

    // TODO: null report type on view mode.
    return fetcher
      .loadLazyLoadingCategories(param)
      .bind(this)
      .then(
        m.action(function(result) {
          var data = result.data;
          var categories = map(function(item) {
            var globalLocalCategory = new CategoryDetail();
            globalLocalCategory.populateDataForGlobalLocalCategory(item);
            return globalLocalCategory;
          }, data);

          var menuCategories;
          if (reportType.toString() === constants.CATEGORY_TYPE.REPORT) {
            menuCategories = this.model.leftMenuReportCategories;
          } else {
            menuCategories = this.model.leftMenuTemplateCategories;
          }

          if (isReset) {
            menuCategories.splice(0, menuCategories.length);
          }

          if (!isEmpty(menuCategories)) {
            menuCategories.shift();
          }

          LazyLoadingUtils.concatTree(
            menuCategories,
            categories,
            constants.LEFT_MENU_CONCAT_TREE_MAP,
            result.numOfChilds,
            []
          );

          menuCategories.splice(0, 0, this.model.createAllItem(this.reportType));
          var dataTree = LazyLoadingUtils.parseToTree(menuCategories, constants.LEFT_MENU_TREE_MAP);

          reportType.toString() === constants.CATEGORY_TYPE.REPORT
            ? (this.model.leftMenuReportCategoryTree = dataTree)
            : (this.model.leftMenuTemplateCategoryTree = dataTree);
          // reset the category'status to has no any new categoríe after reloaded
          this.model.resetCategoryStatus();
        })
      );
  });

  /**
   * search ReportCategories
   * @returns {Promise} array of ReportCategories
   */
  ReportsStore.prototype.lazyLoadingSearch = function(isGlobal, pageInfo) {
    var model = this.items;
    var isReset = isNil(pageInfo);
    pageInfo = pageInfo || this.contentLazyLoadingStore.pageInfoForFirstTime;

    var request = {
      criterias: [],
      isUncategorized: this.isUncategorized,
      sortCriteria: {
        key: model.sortBy,
        descending: model.sortDir === constants.SORT_TYPES.ASC ? false : true
      },
      tenantId: this.tenantId,
      type: this.reportType,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true,
      isGlobal: this.convertIsGlobal(isGlobal)
    };

    // handling criterias
    if (!this.isRootCategory && !isNil(this.selectedCategoryId)) {
      request.criterias.push({
        key: constants.CATEGORY_TYPE.CATEGORY_ID,
        value: this.selectedCategoryId
      });
    }

    (!isNil(model.searchType) || !isNil(model.searchText)) &&
      request.criterias.push({
        key: model.searchType,
        value: model.searchText
      });

    return fetcher.lazyLoadingSearchCategories(request).then(
      function(result) {
        return this.items.populate(result, isReset, constants.REPORT_LIST_CONCAT_TREE_MAP);
      }.bind(this)
    );
  };

  /**
   * Convert isGlobal
   * @param {Boolean} isGlobal
   * @returns {Boolean} has global report
   */
  ReportsStore.prototype.convertIsGlobal = function(isGlobal) {
    switch (isGlobal) {
      case 'null':
        return null;
      case 'false':
        return false;
      case 'true':
        return true;
      case undefined:
        return null;
      default:
        return isGlobal;
    }
  };

  /**
   * Register content list lazy loading store
   * @param {Object} lazyLoadingStore
   */
  ReportsStore.prototype.registerContentLazyLoadingStore = function(lazyLoadingStore) {
    this.contentLazyLoadingStore = lazyLoadingStore;
  };

  /**
   * Register left menu lazy loading store
   * @param {Object} lazyLoadingStore
   */
  ReportsStore.prototype.registerLeftMenuLazyLoadingStore = function(lazyLoadingStore) {
    this.leftMenuLazyLoadingStore = lazyLoadingStore;
  };

  /**
   * Cancel changes to remove draft
   * @return {Promise}
   */
  ReportsStore.prototype.cancelChanges = function() {
    return reportDesignerFetcher.cancelReportChanges(this.selectedReportId);
  };

  module.exports = ReportsStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportsStore.js